import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Mail, Phone, ArrowLeft } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [mode, setMode] = useState("email");
  const [form, setForm] = useState({ email: "", password: "", phone: "", otp: "" });
  const [otpSent, setOtpSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const setF = (p) => setForm((f) => ({ ...f, ...p }));

  const after = (user) => { setUser(user); navigate(user.role === "admin" ? "/admin" : "/"); };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/profile")}`;
  };

  const emailLogin = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/login", { email: form.email, password: form.password }); after(data.user); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const sendOtp = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/phone/request-otp", { phone: form.phone }); setOtpSent(true); if (data.dev_otp) toast.info(`Dev OTP: ${data.dev_otp}`); }
    catch { toast.error("Could not send OTP"); } finally { setBusy(false); }
  };
  const verifyOtp = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/phone/verify-otp", { phone: form.phone, otp: form.otp }); after(data.user); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-md px-5 pt-12 md:pt-20">
      <button onClick={() => navigate(-1)} className="mb-6 flex items-center gap-1 text-sm text-muted-foreground"><ArrowLeft className="h-4 w-4" /> Back</button>
      <h1 className="font-display text-3xl font-semibold tracking-tight">Welcome back</h1>
      <p className="mt-2 text-muted-foreground">Sign in to access your readings.</p>

      <div className="mt-8 space-y-3">
        <Button data-testid="login-google" onClick={google} className="w-full rounded-full border border-border bg-card py-6 text-foreground btn-lift hover:bg-card">Continue with Google</Button>
        <div className="flex gap-2">
          <Button variant={mode === "email" ? "default" : "outline"} onClick={() => setMode("email")} className="flex-1 rounded-full"><Mail className="mr-2 h-4 w-4" /> Email</Button>
          <Button variant={mode === "phone" ? "default" : "outline"} onClick={() => setMode("phone")} className="flex-1 rounded-full"><Phone className="mr-2 h-4 w-4" /> Phone</Button>
        </div>
      </div>

      {mode === "email" ? (
        <div className="mt-5 space-y-3">
          <Input data-testid="login-email" type="email" placeholder="Email" value={form.email} onChange={(e) => setF({ email: e.target.value })} className="rounded-xl bg-background" />
          <Input data-testid="login-password" type="password" placeholder="Password" value={form.password} onChange={(e) => setF({ password: e.target.value })} className="rounded-xl bg-background" />
          <Button data-testid="login-submit" onClick={emailLogin} disabled={busy} className="w-full rounded-full bg-foreground py-6 text-background hover:bg-foreground">Sign in</Button>
        </div>
      ) : (
        <div className="mt-5 space-y-3">
          <Input data-testid="login-phone" placeholder="Phone number" value={form.phone} onChange={(e) => setF({ phone: e.target.value })} className="rounded-xl bg-background" />
          {!otpSent ? (
            <Button data-testid="login-send-otp" onClick={sendOtp} disabled={busy || form.phone.length < 8} className="w-full rounded-full bg-foreground py-6 text-background hover:bg-foreground">Send OTP</Button>
          ) : (
            <>
              <Input data-testid="login-otp" placeholder="6-digit OTP" value={form.otp} onChange={(e) => setF({ otp: e.target.value })} className="rounded-xl bg-background" />
              <Button data-testid="login-verify" onClick={verifyOtp} disabled={busy} className="w-full rounded-full bg-foreground py-6 text-background hover:bg-foreground">Verify & sign in</Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
